/**
 * The telemetry socket.
 *
 * Three things here are deliberate reactions to the predecessor:
 *
 * Auth travels in the subprotocol header, not `?token=`. A query string is
 * written to the access log of every proxy the connection passes through, and
 * an hour-long bearer token for an armed fleet ended up in nginx logs that were
 * shipped off the box for analytics.
 *
 * Every frame goes through `parseServerMessage`. An unrecognised or malformed
 * frame is counted and dropped, never fatal. The predecessor cast socket JSON
 * straight to a domain type and fanned it out through a 90-line switch, so one
 * field the server renamed took the whole connection down with a TypeError in
 * an event handler.
 *
 * Reconnect backs off exponentially with jitter. The predecessor retried every
 * 2 seconds, and twelve browsers open on the same wall display reconnected in
 * lockstep and kept the API pinned while it was trying to come back up.
 */

import { listDrones } from '@/api/drones';
import { WS_BACKOFF, wsUrl } from '@/config';
import { droneActions, type LinkStatus } from '@/stores/droneStore';
import { getAccessToken, useAuthStore } from '@/stores/authStore';
import type { AutopilotMessage } from '@/stores/droneStore';
import type { DroneDelta, DroneId, DroneScalars, TelemetryCache } from '@/types/domain';
import { parseServerMessage, type ServerMessage } from '@/types/ws';

/**
 * The token rides as the second subprotocol: "bearer", "<token>". This is the
 * shape app/security/deps.py parses, and the server echoes "bearer" back in the
 * handshake, which it must, or the browser fails the connection with a protocol
 * error and no diagnostic anywhere.
 */
export const WS_TOKEN_PROTOCOL = 'bearer';

export type FrameListener = (message: ServerMessage) => void;

export interface WsStats {
  framesReceived: number;
  framesRejected: number;
  reconnects: number;
  lastFrameAt: number | null;
}

export interface WsClient {
  connect: () => void;
  disconnect: () => void;
  /** Every validated frame, for callers that need more than the drone store. */
  subscribe: (listener: FrameListener) => () => void;
  stats: () => WsStats;
}

interface Deps {
  url: () => string;
  token: () => string | null;
  setLink: (status: LinkStatus) => void;
  now: () => number;
  socketFactory: (url: string, protocols: string[]) => WebSocket;
  refetchRoster: () => Promise<void>;
  onAccessChanged: () => Promise<void>;
}

async function defaultRefetchRoster(): Promise<void> {
  const result = await listDrones();
  if (result.ok && Array.isArray(result.data)) {
    droneActions.applySnapshot(result.data);
  }
}

/**
 * A drone leaving the user's scope has to be dropped from the store, not merely
 * left to go stale. The roster the server would resend does not mention it, and
 * `applySnapshot` rebuilds membership from what it is given, so the removal
 * happens by reconstruction rather than by anyone remembering to delete it.
 */
async function defaultOnAccessChanged(): Promise<void> {
  await useAuthStore.getState().reloadIdentity();
  await defaultRefetchRoster();
}

const defaultDeps: Deps = {
  url: () => wsUrl(),
  token: getAccessToken,
  setLink: droneActions.setLink,
  now: Date.now,
  socketFactory: (url, protocols) => new WebSocket(url, protocols),
  refetchRoster: defaultRefetchRoster,
  onAccessChanged: defaultOnAccessChanged,
};

function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const raw = WS_BACKOFF.baseMs * 2 ** Math.min(attempt, 10);
  const capped = Math.min(raw, WS_BACKOFF.maxMs);
  // Jitter is symmetric so a fleet of browsers restarted together does not
  // reconnect in lockstep. Twelve clients on a 2s fixed retry kept the API
  // saturated for as long as it took one of them to succeed.
  const spread = capped * WS_BACKOFF.jitter;
  return Math.round(capped - spread + random() * spread * 2);
}

export function createWsClient(overrides: Partial<Deps> = {}): WsClient {
  const deps: Deps = { ...defaultDeps, ...overrides };

  let socket: WebSocket | null = null;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let wanted = false;
  const listeners = new Set<FrameListener>();
  const stats: WsStats = {
    framesReceived: 0,
    framesRejected: 0,
    reconnects: 0,
    lastFrameAt: null,
  };

  function clearRetry(): void {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  }

  function scheduleRetry(): void {
    if (!wanted) return;
    clearRetry();
    const delay = backoffDelay(attempt);
    attempt += 1;
    deps.setLink('reconnecting');
    retryTimer = setTimeout(open, delay);
  }

  function handle(message: ServerMessage): void {
    switch (message.type) {
      case 'snapshot':
        droneActions.applySnapshot(message.drones);
        break;

      case 'delta': {
        const deltas: DroneDelta[] = message.deltas.map((raw) => ({
          droneId: raw.droneId as DroneId,
          scalars: raw.scalars as unknown as DroneScalars,
          telemetry: raw.telemetry as TelemetryCache,
          observedAt: raw.observedAt,
        }));
        droneActions.applyDeltas(deltas);
        break;
      }

      case 'roster':
        if (message.removed.length) droneActions.removeDrones(message.removed as DroneId[]);
        // The roster frame carries ids only, so a new drone still needs its
        // first snapshot. Deltas that arrive before it are held by the store,
        // not dropped.
        if (message.added.length) void deps.refetchRoster();
        break;

      case 'alert':
        droneActions.pushAlert({
          severity: message.severity,
          droneId: message.droneId as DroneId | null,
          message: message.message,
          at: message.at * 1000,
        });
        break;

      case 'access_changed':
        void deps.onAccessChanged();
        break;

      case 'autopilot_message': {
        const raw = message.message;
        const autopilot: AutopilotMessage = {
          droneId: raw.droneId as DroneId,
          severity: raw.severity,
          level: raw.level,
          text: raw.text,
          timestamp: raw.timestamp,
        };
        droneActions.pushAutopilotMessage(autopilot);
        break;
      }
    }

    for (const listener of listeners) {
      try {
        listener(message);
      } catch {
        /* a subscriber must not be able to close the socket */
      }
    }
  }

  function open(): void {
    if (!wanted) return;
    clearRetry();

    const token = deps.token();
    if (!token) {
      // An unauthenticated socket receives nothing, so do not open one. In the
      // predecessor an absent entry in the client map meant "unfiltered", and
      // connecting with no token streamed the entire fleet.
      deps.setLink('closed');
      return;
    }

    deps.setLink(attempt === 0 ? 'connecting' : 'reconnecting');

    let ws: WebSocket;
    try {
      ws = deps.socketFactory(deps.url(), [WS_TOKEN_PROTOCOL, token]);
    } catch {
      scheduleRetry();
      return;
    }
    socket = ws;

    ws.onopen = () => {
      if (attempt > 0) stats.reconnects += 1;
      attempt = 0;
      deps.setLink('open');
    };

    ws.onmessage = (event: MessageEvent) => {
      let raw: unknown;
      try {
        raw = JSON.parse(String(event.data)) as unknown;
      } catch {
        stats.framesRejected += 1;
        return;
      }
      const message = parseServerMessage(raw);
      if (!message) {
        stats.framesRejected += 1;
        return;
      }
      stats.framesReceived += 1;
      stats.lastFrameAt = deps.now();
      handle(message);
    };

    ws.onerror = () => {
      // Nothing to do: a close event always follows, and that is where retry
      // is decided. Handling both double-counted attempts in the predecessor.
    };

    ws.onclose = () => {
      if (socket === ws) socket = null;
      if (!wanted) {
        deps.setLink('closed');
        return;
      }
      scheduleRetry();
    };
  }

  return {
    connect: () => {
      if (wanted) return;
      wanted = true;
      attempt = 0;
      open();
    },

    disconnect: () => {
      wanted = false;
      clearRetry();
      attempt = 0;
      const ws = socket;
      socket = null;
      if (ws) {
        ws.onclose = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onopen = null;
        ws.close(1000, 'client shutdown');
      }
      deps.setLink('closed');
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    stats: () => ({ ...stats }),
  };
}

/** The one socket the app runs on. */
export const wsClient = createWsClient();

/** Exposed for the backoff test. Not part of the runtime surface. */
export const __testing = { backoffDelay };
